import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
 Dialog,
 DialogContent,
 DialogHeader,
 DialogTitle,
 DialogDescription,
 Button,
 ScrollArea,
 DeleteConfirmDialog,
 Label,
 Textarea,
 Select,
 SelectContent,
 SelectItem,
 SelectTrigger,
 SelectValue,
} from 'design-system'
import * as posStore from 'store/posStore'
import * as SyncService from 'services/SyncService'
import { checkConnectivity } from 'utils/connectivityCheck'
import { flushOfflineBootstrapPendingCounts } from 'services/offlineBootstrap'
import { reconcileAndClearSingleFailedPosOrder } from 'services/shiftCloseSyncGate'
import { useAuth } from 'slices/user/hooks'
import { erpToast } from 'design-system/components/erp-toast'
import { RotateCcw, Trash2, RefreshCw, Replace } from 'lucide-react'
import { syncAPI } from 'api/sync'
import { financialAccountsAPI, type FinancialAccountRow } from 'api/financialAccounts'

type Props = {
 open: boolean
 onOpenChange: (open: boolean) => void
 onCountsMayHaveChanged: () => Promise<void>
}

const ACCOUNT_CONFLICT_CODES = new Set([
 'ACCOUNT_NOT_FOUND',
 'FINANCIAL_ACCOUNT_INACTIVE',
 'FINANCIAL_ACCOUNT_ARCHIVED',
 'ACCOUNT_NOT_AVAILABLE_TO_BRANCH',
 'PAYMENT_METHOD_MISMATCH',
 'ACCOUNT_CAPABILITY_MISSING',
 'CASH_DRAWER_MISMATCH',
])

type ReplaceTarget = {
 clientUuid: string
 branchId: string
 lineId: string
 method: string
 originalAccountId: string | null
}

export function FailedOfflineOrdersDialog({ open, onOpenChange, onCountsMayHaveChanged }: Props) {
 const { t } = useTranslation()
 const { hasPermission, isSuperAdmin } = useAuth()
 const canManageShifts = isSuperAdmin || hasPermission('manage_shifts')
 const canReplaceAccount =
  isSuperAdmin ||
  hasPermission('resolve_offline_financial_conflict') ||
  hasPermission('manage_shifts') ||
  hasPermission('access_admin_routes')
 const [items, setItems] = useState<posStore.FailedOfflineOrder[]>([])
 const [loading, setLoading] = useState(false)
 const [busyUuid, setBusyUuid] = useState<string | null>(null)
 const [dismissUuid, setDismissUuid] = useState<string | null>(null)
 /** Survives Radix close/re-render timing so removal always uses the intended client_uuid. */
 const dismissTargetRef = useRef<string | null>(null)
 const [retryAllBusy, setRetryAllBusy] = useState(false)
 const [replaceTarget, setReplaceTarget] = useState<ReplaceTarget | null>(null)
 const [replaceAccounts, setReplaceAccounts] = useState<FinancialAccountRow[]>([])
 const [replacementId, setReplacementId] = useState('')
 const [replaceReason, setReplaceReason] = useState('')
 const [replaceBusy, setReplaceBusy] = useState(false)

 const reload = useCallback(async () => {
  const list = await posStore.getFailedOrders()
  setItems(list)
 }, [])

 useEffect(() => {
  if (!open) return
  void reload()
 }, [open, reload])

 useEffect(() => {
  const onEvt = () => {
   void reload()
  }
  window.addEventListener('pos:failed-orders-updated', onEvt)
  return () => window.removeEventListener('pos:failed-orders-updated', onEvt)
 }, [reload])

 const refreshBootstrap = async () => {
  await flushOfflineBootstrapPendingCounts()
  await onCountsMayHaveChanged()
  if (typeof window !== 'undefined') {
   window.dispatchEvent(new Event('pos:failed-orders-updated'))
  }
 }

 const handleRetryOne = async (clientUuid: string) => {
  if (!(await checkConnectivity())) {
   erpToast.warning(t('common.errors.noInternet'))
   return
  }
  setBusyUuid(clientUuid)
  try {
   const moved = await posStore.moveFailedOrderToPending(clientUuid)
   if (!moved.ok) {
    if (moved.error === 'already_pending') {
     erpToast.warning(t('common.failedOfflineOrders.alreadyPending'))
    } else {
     erpToast.error(t('common.failedOfflineOrders.retryQueueFailed'))
    }
    return
   }
   const result = await SyncService.pushPending()
   await reload()
   await refreshBootstrap()
   const stillFailed = (await posStore.getFailedOrders()).some((f) => f.order.client_uuid === clientUuid)
   if (stillFailed) {
    erpToast.warning(t('common.failedOfflineOrders.syncRetryFailed'))
   } else {
    erpToast.success(t('common.failedOfflineOrders.syncRetrySuccess'))
   }
   if (result.errors.length > 0) {
    erpToast.message(result.errors.slice(0, 2).join('· '), { duration: 5500 })
   }
  } finally {
   setBusyUuid(null)
  }
 }

 const handleRetryAll = async () => {
  if (!(await checkConnectivity())) {
   erpToast.warning(t('common.errors.noInternet'))
   return
  }
  setRetryAllBusy(true)
  try {
   const n = await posStore.moveAllFailedOrdersToPending()
   if (n === 0) {
    erpToast.info(t('common.failedOfflineOrders.noFailedToRetry'))
    return
   }
   const result = await SyncService.pushPending()
   await reload()
   await refreshBootstrap()
   const remaining = await posStore.getFailedOrders()
   if (remaining.length === 0) {
    erpToast.success(t('common.failedOfflineOrders.retryAllSuccess'))
   } else {
    erpToast.warning(
     t('common.failedOfflineOrders.retryAllPartial', { moved: n, remaining: remaining.length }),
     { duration: 6500 },
    )
   }
   if (result.errors.length > 0) {
    erpToast.message(result.errors.slice(0, 3).join('· '), { duration: 6000 })
   }
  } finally {
   setRetryAllBusy(false)
  }
 }

 const openReplace = async (row: posStore.FailedOfflineOrder) => {
  if (!canReplaceAccount) {
   erpToast.error(t('common.failedOfflineOrders.replaceRequiresPermission'))
   return
  }
  if (!(await checkConnectivity())) {
   erpToast.warning(t('common.errors.noInternet'))
   return
  }
  const lines = posStore.normalizePaymentLines(row.order.payment_lines) ?? []
  const line =
   lines.find((l) => l.client_line_id) ??
   lines[0]
  if (!line?.client_line_id) {
   erpToast.error(t('common.failedOfflineOrders.replaceMissingLine'))
   return
  }
  const method = String(line.payment_method ?? row.order.payment_type ?? '').toLowerCase()
  setReplaceBusy(true)
  try {
   const res = await financialAccountsAPI.available(row.order.branch_id, 'sales', method || undefined)
   setReplaceAccounts(Array.isArray(res.data) ? res.data : [])
   setReplacementId('')
   setReplaceReason('')
   setReplaceTarget({
    clientUuid: row.order.client_uuid,
    branchId: row.order.branch_id,
    lineId: String(line.client_line_id),
    method,
    originalAccountId: line.financial_account_id ? String(line.financial_account_id) : null,
   })
  } catch {
   erpToast.error(t('common.failedOfflineOrders.replaceLoadAccountsFailed'))
  } finally {
   setReplaceBusy(false)
  }
 }

 const confirmReplace = async () => {
  if (!replaceTarget || replaceBusy) return
  const reason = replaceReason.trim()
  if (!replacementId || reason.length < 3) {
   erpToast.warning(t('common.failedOfflineOrders.replaceReasonRequired'))
   return
  }
  setReplaceBusy(true)
  try {
   await syncAPI.replaceOfflinePaymentAccount(
    replaceTarget.clientUuid,
    {
     client_line_id: replaceTarget.lineId,
     replacement_financial_account_id: replacementId,
     reason,
    },
    replaceTarget.branchId,
   )
   await posStore.updateFailedOrderPaymentAccount(
    replaceTarget.clientUuid,
    replaceTarget.lineId,
    replacementId,
   )
   const uuid = replaceTarget.clientUuid
   setReplaceTarget(null)
   erpToast.success(t('common.failedOfflineOrders.replaceSuccess'))
   await handleRetryOne(uuid)
  } catch (e: unknown) {
   const msg =
    (e as { response?: { data?: { message?: string } } })?.response?.data?.message ||
    t('common.failedOfflineOrders.replaceFailed')
   erpToast.error(String(msg))
  } finally {
   setReplaceBusy(false)
  }
 }

 const confirmDismiss = async () => {
  const uuid = dismissTargetRef.current ?? dismissUuid
  dismissTargetRef.current = null
  setDismissUuid(null)
  if (!uuid || !uuid.trim()) {
   return
  }
  if (!canManageShifts) {
   erpToast.error(t('pos.sync.reconcileRequiresManager'))
   return
  }
  if (!(await checkConnectivity())) {
   erpToast.warning(t('common.errors.noInternet'))
   return
  }
  const normalized = uuid.trim()
  const entry = items.find((row) => row.order.client_uuid === normalized)
  if (!entry) {
   erpToast.error(t('common.failedOfflineOrders.dismissFailed'))
   return
  }
  setLoading(true)
  try {
   const result = await reconcileAndClearSingleFailedPosOrder(entry, { hasManageShifts: true })
   await reload()
   await refreshBootstrap()
   if (result.cleared) {
    erpToast.success(t('common.failedOfflineOrders.dismissSuccess'))
   } else if (result.error === 'unreconciled') {
    erpToast.warning(t('pos.sync.failedStillUnreconciled', { count: 1 }))
   } else if (result.error === 'permission') {
    erpToast.error(t('pos.sync.reconcileRequiresManager'))
   } else if (result.error === 'offline') {
    erpToast.warning(t('common.errors.noInternet'))
   } else {
    erpToast.error(t('common.failedOfflineOrders.dismissFailed'))
   }
  } catch (e) {
   console.error(' dismiss failed offline order', e)
   erpToast.error(t('common.failedOfflineOrders.dismissFailed'))
  } finally {
   setLoading(false)
  }
 }

 const formatCustomer = (o: posStore.PendingOfflineOrder) => {
  if (o.customer_id != null) return t('common.failedOfflineOrders.customerId', { id: o.customer_id })
  return '—'
 }

 const showReplaceAction = (row: posStore.FailedOfflineOrder) => {
  if (!canReplaceAccount) return false
  const code = row.order.conflict_code
  if (code && ACCOUNT_CONFLICT_CODES.has(code)) return true
  const lines = posStore.normalizePaymentLines(row.order.payment_lines) ?? []
  return lines.some((l) => !!l.client_line_id && !!l.financial_account_id)
 }

 return (
  <>
   <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="max-w-xl max-h-[85vh] flex flex-col gap-3" dir="rtl">
     <DialogHeader>
      <DialogTitle>{t('common.failedOfflineOrders.title')}</DialogTitle>
      <DialogDescription>
       {canManageShifts
        ? t('common.failedOfflineOrders.description')
        : t('common.failedOfflineOrders.descriptionManagerOnlyDismiss')}
      </DialogDescription>
     </DialogHeader>

     <div className="flex flex-wrap gap-2">
      <Button
       type="button"
       variant="secondary"
       size="sm"
       disabled={retryAllBusy || items.length === 0}
       onClick={() => {
        void handleRetryAll()
       }}
       className="gap-1.5"
      >
       {retryAllBusy ? (
        <RefreshCw className="h-3.5 w-3.5 animate-spin" />
       ) : (
        <RefreshCw className="h-3.5 w-3.5" />
       )}
       {t('common.failedOfflineOrders.retryAll')}
      </Button>
     </div>

     <ScrollArea className="flex-1 min-h-[200px] max-h-[55vh] rounded-md border border-border-subtle p-2">
      {items.length === 0 ? (
       <p className="text-sm text-muted-foreground text-center py-8">{t('common.failedOfflineOrders.empty')}</p>
      ) : (
       <ul className="flex flex-col gap-3">
        {items.map((row) => (
         <li
          key={row.order.client_uuid}
          className=" rounded-lg border border-border-subtle bg-muted p-3 text-sm space-y-1.5"
         >
          <div className="font-mono text-xs break-all text-foreground/90">{row.order.client_uuid}</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-3 gap-y-1 text-xs text-muted-foreground">
           <span>
            {t('common.failedOfflineOrders.branchLabel')} {row.order.branch_id}
           </span>
           <span>
            {t('common.failedOfflineOrders.createdLocally')} {row.order.created_at_local ?? row.failedAt}
           </span>
           <span>
            {t('common.failedOfflineOrders.lastAttempt')} {row.lastAttemptAt ?? row.failedAt}
           </span>
           <span>
            {t('common.failedOfflineOrders.retryCount')} {row.retryCount}
           </span>
           <span>{formatCustomer(row.order)}</span>
           <span>
            {t('common.failedOfflineOrders.shiftId')}{' '}
            {row.order.shift_id ? row.order.shift_id : t('common.failedOfflineOrders.shiftIdMissing')}
           </span>
           <span>
            {t('common.failedOfflineOrders.total')}{' '}
            {typeof row.order.total === 'number' ? row.order.total.toFixed(2) : '—'}
           </span>
          </div>
          <p className="text-xs text-destructive font-medium leading-snug">{row.reason}</p>
          {row.order.conflict_code ? (
           <p className="text-xs font-mono text-muted-foreground">{row.order.conflict_code}</p>
          ) : null}
          <div className="flex flex-wrap gap-2 pt-1">
           <Button
            type="button"
            size="sm"
            variant="default"
            disabled={busyUuid !== null || replaceBusy}
            className="gap-1"
            onClick={() => {
             void handleRetryOne(row.order.client_uuid)
            }}
           >
            {busyUuid === row.order.client_uuid ? (
             <RefreshCw className="h-3.5 w-3.5 animate-spin" />
            ) : (
             <RotateCcw className="h-3.5 w-3.5" />
            )}
            {t('common.retry')}
           </Button>
           {showReplaceAction(row) ? (
            <Button
             type="button"
             size="sm"
             variant="secondary"
             disabled={busyUuid !== null || replaceBusy}
             className="gap-1"
             onClick={() => {
              void openReplace(row)
             }}
            >
             <Replace className="h-3.5 w-3.5" />
             {t('common.failedOfflineOrders.replaceAccount')}
            </Button>
           ) : null}
           {canManageShifts ? (
            <Button
             type="button"
             size="sm"
             variant="outline"
             disabled={busyUuid !== null || loading}
             className="gap-1 text-destructive border-destructive/40"
             onClick={() => {
              const id = String(row.order.client_uuid ?? '').trim()
              dismissTargetRef.current = id
              setDismissUuid(id)
             }}
            >
             <Trash2 className="h-3.5 w-3.5" />
             {t('common.failedOfflineOrders.dismissReconcile')}
            </Button>
           ) : null}
          </div>
         </li>
        ))}
       </ul>
      )}
     </ScrollArea>
    </DialogContent>
   </Dialog>

   <Dialog
    open={replaceTarget != null}
    onOpenChange={(v) => {
     if (!v && !replaceBusy) setReplaceTarget(null)
    }}
   >
    <DialogContent className="max-w-md" dir="rtl">
     <DialogHeader>
      <DialogTitle>{t('common.failedOfflineOrders.replaceTitle')}</DialogTitle>
      <DialogDescription>{t('common.failedOfflineOrders.replaceDescription')}</DialogDescription>
     </DialogHeader>
     <div className="space-y-3 text-sm">
      <div>
       <Label>{t('common.failedOfflineOrders.replaceAccountLabel')}</Label>
       <Select value={replacementId} onValueChange={setReplacementId}>
        <SelectTrigger className="mt-1">
         <SelectValue placeholder={t('common.failedOfflineOrders.replaceSelectPlaceholder')} />
        </SelectTrigger>
        <SelectContent>
         {replaceAccounts
          .filter((a) => String(a.id) !== String(replaceTarget?.originalAccountId ?? ''))
          .map((a) => (
           <SelectItem key={a.id} value={String(a.id)}>
            {a.name} ({a.payment_method})
           </SelectItem>
          ))}
        </SelectContent>
       </Select>
      </div>
      <div>
       <Label>{t('common.failedOfflineOrders.replaceReasonLabel')}</Label>
       <Textarea
        className="mt-1"
        value={replaceReason}
        onChange={(e) => setReplaceReason(e.target.value)}
        rows={3}
       />
      </div>
      <div className="flex justify-end gap-2">
       <Button type="button" variant="outline" disabled={replaceBusy} onClick={() => setReplaceTarget(null)}>
        {t('common.cancel')}
       </Button>
       <Button type="button" disabled={replaceBusy} onClick={() => void confirmReplace()}>
        {replaceBusy ? <RefreshCw className="h-3.5 w-3.5 animate-spin" /> : null}
        {t('common.failedOfflineOrders.replaceConfirm')}
       </Button>
      </div>
     </div>
    </DialogContent>
   </Dialog>

   <DeleteConfirmDialog
    open={dismissUuid != null}
    onOpenChange={(v) => {
     if (!v) setDismissUuid(null)
    }}
    onCancel={() => {
     dismissTargetRef.current = null
    }}
    onConfirm={() => {
     void confirmDismiss()
    }}
    title={t('common.failedOfflineOrders.confirmDismissTitle')}
    description={t('common.failedOfflineOrders.confirmDismissReconcileDescription')}
    confirmText={t('common.failedOfflineOrders.confirmDismissConfirm')}
    cancelText={t('common.cancel')}
    loading={loading}
   />
  </>
 )
}

export default FailedOfflineOrdersDialog
